// Sign-in API client (same origin, /auth/v1/*). CloudFront signs requests to
// the sign-in Lambda, so POST bodies carry their SHA-256 in
// x-amz-content-sha256 and the session token goes in x-k8hq-token.
export interface Tokens { accessToken: string; accessExpiresAt: string; refreshToken: string }
export interface Session { userId: string; tokens: Tokens }

export class ApiError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}

async function sha256Hex(s: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return [...new Uint8Array(d)].map(b => b.toString(16).padStart(2, "0")).join("");
}

export async function post<T>(path: string, body: unknown, token?: string, f: typeof fetch = fetch): Promise<T> {
  const text = JSON.stringify(body ?? {});
  const headers: Record<string, string> = { "content-type": "application/json", "x-amz-content-sha256": await sha256Hex(text) };
  if (token) headers["x-k8hq-token"] = token;
  const res = await f(`/auth${path}`, { method: "POST", headers, body: text, credentials: "omit" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error ?? `request failed (${res.status})`);
  return data as T;
}

// The refresh token is kept across reloads; the access token only in memory.
const REFRESH_KEY = "k8hq.refresh";
export const store = {
  get refresh(): string | null { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } },
  set refresh(v: string | null) { try { v ? localStorage.setItem(REFRESH_KEY, v) : localStorage.removeItem(REFRESH_KEY); } catch { /* private mode */ } },
};
